import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ButtonV2 } from '@zola/zola-ui';
import _ from 'lodash';
import { updateExternal, placeOrder, confirmOrder, cancelOrder, resetExternal } from '../../actions/ExternalActions';
import { search } from '../../actions/RegistryCollectionActions';
import ProductPrice from '../product/ProductPrice';
import ExternalPurchaseForm from './ExternalPurchaseForm';
import DifferentStoreForm from './DifferentStoreForm';
import DidntBuyForm from './DidntBuyForm';
import OnlineOrStoreForm from './OnlineOrStoreForm';
import SameStoreForm from './SameStoreForm';
import { showModal, hideModal } from '../../actions/ModalActions';


const QuantitySelect = ({ quantities, onSelectQuantity, selected }) => (
  <select value={selected} className="form-control input-inline" onChange={onSelectQuantity} required>
    {quantities.map(option => (
      <option key={option} value={option} label={option}>{option}</option>
    ))}
  </select>
);

QuantitySelect.propTypes = {
  quantities: PropTypes.arrayOf(PropTypes.number),
  selected: PropTypes.number,
  onSelectQuantity: PropTypes.func
};


class ExternalPurchaseFlow extends Component {
  constructor(props) {
    super(props);

    this.placeExternalOrder = this.placeExternalOrder.bind(this);
    this.goToStep = this.goToStep.bind(this);
    this.goBack = this.goBack.bind(this);
    this.selectQuantity = this.selectQuantity.bind(this);
    this.confirmExternalOrder = this.confirmExternalOrder.bind(this);
    this.storeTypeSubmit = this.storeTypeSubmit.bind(this);
    this.cancelOrderRequest = this.cancelOrderRequest.bind(this);

    this.state = {
      currentStep: 'INIT',
      stepHistory: [],
      quantity: 1,
    };
  }

  componentDidMount() {
    const { userContext, onUpdateExternal } = this.props;
    if (!userContext.is_guest) {
      const giftGiverEmail = userContext.email;
      const giftGiverName = (userContext.first_name && userContext.last_name) ? `${userContext.first_name} ${userContext.last_name}` : '';
      onUpdateExternal({ giftGiverEmail, giftGiverName });
    }
  }

  componentWillUnmount() {
    const { onResetExternal, onSearch } = this.props;
    onResetExternal();
    onSearch();
  }

  goToStep(step) {
    this.setState((state) => {
      const { stepHistory } = state;
      stepHistory.push(state.currentStep);
      return {
        currentStep: step,
        stepHistory
      };
    });
  }

  goBack() {
    const { stepHistory } = this.state;
    const last = stepHistory.pop();
    this.setState({
      currentStep: last,
      stepHistory
    });
  }

  placeExternalOrder(formValues) {
    const { collectionItem, onPlaceOrderRequest, onUpdateExternal } = this.props;

    const externalPurchaseRequest = {
      collectionItemId: collectionItem.object_id,
      quantity: collectionItem.contributions.still_needs,
      giftGiverEmail: formValues.giftGiverEmail,
      isConfirmed: false,
      purchasedInStore: false,
      itemOrigin: 'WEDDINGS'
    };

    const newWindow = window.open('', '_blank');
    onPlaceOrderRequest(externalPurchaseRequest).then(() => {
      this.setState({ currentStep: 'DIDYOUBUY' });
      onUpdateExternal({ giftGiverEmail: formValues.giftGiverEmail });
      newWindow.location.href = `/registry/collection-item/${collectionItem.object_id}/external-redirect`;
    });
  }

  confirmExternalOrder(formValues) {
    const { external, collectionItem, onConfirmOrderRequest, onPlaceOrderRequest, onResetExternal, onSearch } = this.props;
    const { giftGiverEmail, giftGiverName, quantity, purchasedInStore, orderNumber, externalStoreName } = Object.assign({}, external, formValues);
    if (external.splitOrderId) {
      const confirmRequest = { quantity, giftGiverName, purchasedInStore, externalStoreName, orderNumber };
      onConfirmOrderRequest(external.splitOrderId, external.token, confirmRequest).then(() => onSearch());
    } else {
      const placeOrderRequest = { quantity, giftGiverEmail, collectionItemId: collectionItem.object_id, giftGiverName, externalStoreName, isConfirmed: true, orderNumber, purchasedInStore, itemOrigin: 'WEDDINGS' };
      onPlaceOrderRequest(placeOrderRequest).then(() => onSearch());
    }
    onResetExternal();
    this.goToStep('FINISHED');
  }

  cancelOrderRequest(formValues) {
    const { external, onCancelOrderRequest, openModal, closeModal } = this.props;
    const { splitOrderId, token } = external;
    const { reason } = formValues;

    onCancelOrderRequest(splitOrderId, token, reason).then(() => {
      closeModal();
      openModal('DIDNT_BUY_MODAL');
    });
  }

  selectQuantity(e) {
    const { onUpdateExternal } = this.props;
    const { value } = e.currentTarget.selectedOptions[0];

    onUpdateExternal({ quantity: parseInt(value, 10) });
    this.setState({
      quantity: parseInt(value, 10)
    });
  }

  storeTypeSubmit(formValues) {
    const { onUpdateExternal } = this.props;
    const { purchasedInStore, noOrderNum } = formValues;
    if (noOrderNum) {
      onUpdateExternal({ purchasedInStore, orderNumber: null, noOrderNum });
    } else {
      onUpdateExternal(formValues);
    }
    this.goToStep('SAMESTORE');
  }

  render() {
    const { collectionItem, registry, closeModal, personalNote } = this.props;
    const { currentStep, quantity } = this.state;

    const stillNeeds = collectionItem.contributions.still_needs;
    const isFulfilled = collectionItem.contributions.show_as_fulfilled;
    const isReserved = collectionItem.contributions.reserved;
    const showContributions = !collectionItem.contributions.hide_contributions;

    const quantities = _.range(1, parseInt(stillNeeds, 10) + 1);

    return (
      <div id="external-flow">
        {currentStep === 'INIT' && (
          <div>
            <div className="shop-entity-details shop-entity-details-big shop-entity-details-external">
              <h6>
                {collectionItem.brand && (
                  <a className="brand-name" href={`/shop/brand/${collectionItem.brand.key}`} target="_blank" rel="noreferrer noopener">{collectionItem.brand.name} &rsaquo;</a>
                )}
              </h6>
              <h2 className="entity-name">{collectionItem.name}</h2>
              {showContributions && (
                <div className="price-container">
                  <h3 className="price"><ProductPrice sku={collectionItem} /></h3>
                  <p className="external-store small"> on {collectionItem.store_name}</p>
                </div>
              )}
            </div>
            {/* External Checkout Purchase */}
            {!isFulfilled && !isReserved && (
              <div>
                <div className="text-secondary contributions">still needs: {stillNeeds}</div>
                <div className="padding-bottom external external-flow__button-container">
                  <ButtonV2 fullWidth className="margin-bottom-2x" onClick={() => this.goToStep('BEFOREYOULEAVE')}>
                    Buy on {collectionItem.store_name}
                  </ButtonV2>
                  <ButtonV2 fullWidth variant="secondary" className="margin-bottom-2x" onClick={() => this.goToStep('QTY')}>
                    I bought this already
                  </ButtonV2>
                  {personalNote && <section className="hidden-md hidden-lg">{personalNote}</section>}
                </div>
                <div className="free-shipping-excluded">
                  <div className="value-label small">The price and product info you see here may be different from what’s listed on the other site. Please note: If you buy this product from the other site, Zola benefits will not apply. <a href="https://help.zola.com/hc/en-us/sections/115000736191-Add-Anything" target="_blank" rel="noopener noreferrer">Find Out Why</a></div>
                </div>
                <div className="mobile-button-container">
                  <ButtonV2 className="margin-bottom-2x" onClick={() => this.goToStep('BEFOREYOULEAVE')}>
                      Buy on {collectionItem.store_name}
                  </ButtonV2>
                  <ButtonV2 variant="secondary" className="margin-bottom-2x" onClick={() => this.goToStep('QTY')}>
                    I bought this already
                  </ButtonV2>
                </div>
              </div>
            )}
            {/* fulfilled message */}
            {isFulfilled && (
              <div className="fulfilled-message">
                <h5 className="registry-item-status fulfilled">
                  <span className="zolaicon zolaicon-checkmark" />
                  Purchased
                </h5>
              </div>
            )}
            {isReserved && (
              <div className="fulfilled-message">
                <h5 className="registry-item-status fulfilled">
                  <span className="zolaicon zolaicon-checkmark" />
                  On Hold
                </h5>
                <div className="padding-top-2x">
                  This gift is reserved until we confirm a guest bought this gift. If you&apos;re the guest who bought it, look for a confirmation email from us, so we can update the couple&apos;s registry.
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 'BEFOREYOULEAVE' && (
          <div>
            <div className="text-button" onClick={this.goBack}>&lsaquo; Back</div>
            <div className="external-title">
              <h2>What&#39;s your email address?</h2>
              <p>We&#39;ll reserve this gift and make sure no one else tries to buy it until we confirm if you&#39;ve bought it.</p>
              <ExternalPurchaseForm onSubmit={this.placeExternalOrder} storeName={collectionItem.store_name} shippingAddress={registry.address_view} stillNeeds={stillNeeds} />
            </div>
          </div>
        )}

        {currentStep === 'DIDYOUBUY' && (
          <div>
            <div className="external-title">
              <h2>Did you buy this gift?</h2>
              <p>This info will help {registry.registrant_first_name} &amp; {registry.partner_registrant_first_name} keep track of all their gifts.</p>
              <div className="still-needs padding-top">still needs: {stillNeeds}</div>
            </div>
            <div className="padding-bottom">
              <ButtonV2 className="margin-bottom-2x" onClick={() => this.goToStep('QTY')}>Yes, I bought it</ButtonV2>
              <ButtonV2 variant="secondary" className="margin-bottom-2x separator--one" onClick={() => this.goToStep('DIDNTBUY')}>No, I Didn&#39;t</ButtonV2>
            </div>
            <div className="still-want">
              <span className="h4">Still want to buy it? </span>
              <span>
                Return to <a role="button" href={`/registry/collection-item/${collectionItem.object_id}/external-redirect`} target="_blank" rel="noopener noreferrer">{collectionItem.store_name}</a>
              </span>
            </div>

            {registry.address_view && (
              <div className="copy-address">
                <h4>Copy this address if you&#39;d like to ship it to them directly</h4>
                <div>
                  <div className="value-label">
                    {registry.address_view.first_name} {registry.address_view.last_name}<br />
                    {registry.address_view.street_address_1}<br />
                    {registry.address_view.street_address_2 && (
                      <span>{registry.address_view.street_address_2}<br /></span>
                    )}
                    {registry.address_view.city}, {registry.address_view.state_province_region} {registry.address_view.postal_code}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 'DIDNTBUY' && (
          <div>
            <div className="text-button" onClick={this.goBack}>&lsaquo; Back</div>
            <div className="external-title">
              <h2>Do you mind telling us why?</h2>
            </div>
            <DidntBuyForm onSubmit={this.cancelOrderRequest} onChangeAction={this.toggleRadioField} />
          </div>
        )}

        {currentStep === 'QTY' && (
          <div className="qty-step">
            <div className="text-button" onClick={this.goBack}>&lsaquo; Back</div>
            <div className="external-title">
              <h2>Great, let&#39;s update their registry!</h2>
              <p>This info will help {registry.registrant_first_name} &amp; {registry.partner_registrant_first_name} keep track of all their gifts.</p>
            </div>
            <h4>How many of these did you buy and where?</h4>
            {stillNeeds > 1 ? (
              <div className="qty text-large">I bought <QuantitySelect quantities={quantities} selected={quantity} onSelectQuantity={this.selectQuantity} /> at</div>
            ) : (
              <div className="qty text-large">I bought 1 at</div>
            )}
            <div className="store-buttons margin-top-2x">
              <ButtonV2 className="btn-left" onClick={() => this.goToStep('ONLINEORSTORE')}>{collectionItem.store_name}</ButtonV2>
              <ButtonV2 variant="secondary" onClick={() => this.goToStep('DIFFERENTSTORE')}>A different store</ButtonV2>
            </div>
          </div>
        )}

        {currentStep === 'ONLINEORSTORE' && (
          <div className="online-or-store-step">
            <div className="text-button" onClick={this.goBack}>&lsaquo; Back</div>
            <div className="external-title">
              <h2>Did you buy it online or in store?</h2>
              <p>This info will help {registry.registrant_first_name} &amp; {registry.partner_registrant_first_name} keep track of all their gifts</p>
            </div>
            <OnlineOrStoreForm onSubmit={this.storeTypeSubmit} storeName={collectionItem.store_name} />
          </div>
        )}

        {currentStep === 'SAMESTORE' && (
          <div>
            <div className="text-button" onClick={this.goBack}>&lsaquo; Back</div>
            <div className="external-title">
              <h2>Who should we say this gift is from?</h2>
              <p>This info will help {registry.registrant_first_name} &amp; {registry.partner_registrant_first_name} keep track of all their gifts.</p>
            </div>
            <SameStoreForm onSubmit={this.confirmExternalOrder} />
          </div>
        )}

        {currentStep === 'DIFFERENTSTORE' && (
          <div>
            <div className="text-button" onClick={this.goBack}>&lsaquo; Back</div>
            <div className="external-title">
              <h2>Where&#39;d you buy it?</h2>
              <p>This info will help {registry.registrant_first_name} &amp; {registry.partner_registrant_first_name} keep track of all their gifts</p>
            </div>
            <DifferentStoreForm onSubmit={this.confirmExternalOrder} />
          </div>
        )}

        {currentStep === 'FINISHED' && (
          <div>
            <div className="external-title">
              <h2>All Set</h2>
              <p>{registry.registrant_first_name} &amp; {registry.partner_registrant_first_name} have been notified of your gift.</p>
            </div>
            <ButtonV2 onClick={closeModal}>Continue Shopping</ButtonV2>
          </div>
        )}
      </div>
    );
  }
}

ExternalPurchaseFlow.propTypes = {
  collectionItem: PropTypes.shape({}),
  registry: PropTypes.shape({}),
  userContext: PropTypes.shape({}),
  external: PropTypes.shape({}),
  personalNote: PropTypes.element,
  onPlaceOrderRequest: PropTypes.func,
  onConfirmOrderRequest: PropTypes.func,
  onCancelOrderRequest: PropTypes.func,
  onUpdateExternal: PropTypes.func,
  onResetExternal: PropTypes.func,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  onSearch: PropTypes.func,
};

const mapStateToProps = state => ({
  external: state.external,
  userContext: state.user.userContext
});

const mapDispatchToProps = dispatch => ({
  onUpdateExternal: values => dispatch(updateExternal(values)),
  onPlaceOrderRequest: externalPurchaseRequest => dispatch(placeOrder(externalPurchaseRequest)),
  onConfirmOrderRequest: (splitOrderId, token, body) => dispatch(confirmOrder(splitOrderId, token, body)),
  onCancelOrderRequest: (splitOrderId, token, reason) => dispatch(cancelOrder(splitOrderId, token, reason)),
  onResetExternal: () => dispatch(resetExternal()),
  openModal: modalType => dispatch(showModal(modalType, {}, { size: 'sm' })),
  closeModal: () => {
    dispatch(hideModal());
  },
  onSearch: () => dispatch(search())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExternalPurchaseFlow);



// WEBPACK FOOTER //
// ./src/components/registryitem/ExternalPurchaseFlow.jsx